import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { LoggerModule, NGXLogger } from 'ngx-logger';
import { environment } from '../environments/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { WebpackTranslateLoader } from './app.utils';
import {
  InMemoryScrollingOptions,
  provideRouter,
  withInMemoryScrolling,
} from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { GlobalErrorHandlerService } from './core/services/global-error-handler.service';
import { DatePipe } from '@angular/common';
import { Translations } from './core/services/translations.service';
import { TransactionInterceptor } from './core/interceptor/transaction.intercepter';
import { AppInitService } from './app-init.service';
import { providePrimeNG } from 'primeng/config';
import MaterialPreset from '@primeuix/themes/material';
import { provideNgxMask } from 'ngx-mask';
import { routes } from './app.routes';
import { DynatraceLogger } from './core/services/dynatrace-logger.service';
import { lastValueFrom } from 'rxjs';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';

const scrollOptions: InMemoryScrollingOptions = {
  // Automatic restoration of scroll position when navigating between routes.
  scrollPositionRestoration: 'enabled',
};

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      LoggerModule.forRoot({
        serverLoggingUrl: 'api/logs',
        level: environment.loggingLevel,
        serverLogLevel: environment.serverLoggingLevel,
        timestampFormat: environment.timestampFormat,
        disableConsoleLogging: environment.disableConsoleLogging,
        disableFileDetails: true,
      }),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useClass: WebpackTranslateLoader,
        },
      }),
    ),

    provideRouter(routes, withInMemoryScrolling(scrollOptions)),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),

    // Override NGXLogger with DynatraceLogger to automatically report errors to Dynatrace
    {
      provide: NGXLogger,
      useClass: DynatraceLogger,
    },

    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    DatePipe,
    Translations,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TransactionInterceptor,
      multi: true,
    },
    AppInitService,
    provideAppInitializer(() => {
      const appInitService = inject(AppInitService);
      return lastValueFrom(appInitService.initialize());
    }),
    providePrimeNG({
      theme: {
        preset: MaterialPreset,
        options: {
          darkModeSelector: false,
          colorMode: 'blue',
        },
      },
    }),
    provideNgxMask(),
  ],
};
